import { useConversation } from '@elevenlabs/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { convertJapaneseNumbersToRoman } from '@/utils/japaneseNumberConverter';

export enum CallState {
  INCOMING = 'incoming',
  ACTIVE = 'active',
  ENDED = 'ended',
}

export enum LoadingState {
  INITIAL = 'initial',
  CONNECTING = 'connecting',
  READY = 'ready',
  ACTIVE = 'active',
  ENDING = 'ending',
  ERROR = 'error',
}

export interface UseHRAgentCallProps {
  userId?: string;
  onCallEnded?: () => void;
  persona?: string;
  company?: string;
}

export interface Transcript {
  source: string;
  message: string;
  timestamp: Date;
}

// Persona configurations
interface PersonaConfig {
  voiceId: string;
  avatar: string;
  name: string;
  systemPrompt: string;
}

interface CompanyConfig {
  agentId: string;
  language: string;
  displayName: string;
  getInitialPrompt: () => string;
  personas: Record<string, PersonaConfig>;
  defaultPersona: string;
}

// Get greeting based on current time (Japanese)
const getJapaneseTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'おはようございます';
  if (hour < 18) return 'こんにちは';
  return 'こんばんは';
};

// Get greeting based on current time (English)
const getEnglishTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// Company configurations
const COMPANY_CONFIGS: Record<string, CompanyConfig> = {
  coupang: {
    agentId: 'agent_01jzj7q91weg2bjs2yf073hegz',
    language: 'ja',
    displayName: 'Coupang人事サポート',
    getInitialPrompt: () => `${getJapaneseTimeBasedGreeting()}。Coupang人事サポートです。`,
    defaultPersona: 'female',
    personas: {
      female: {
        voiceId: 'RBnMinrYKeccY3vaUxlZ',
        avatar: 'https://dopmo1eihgbgm.cloudfront.net/686badd644e547250ce1e575/hr-japanese.webp',
        name: '田中美咲',
        systemPrompt: `あなたはCoupang Japanの人事サポートセンターの担当者、田中美咲（たなかみさき）です。
従業員からの電話を受けて、給与・コミッション・労務に関する質問に親切で丁寧に対応してください。

【話し方の指示】
- 自然で流暢な日本語を使う
- 「ええと」「そうですね」「はい」などの相槌を自然に入れる
- 相手の話をしっかり聞いて、共感的に応答する
- 明るく親しみやすい口調を心がける
- 専門用語は避けて、分かりやすい言葉で説明する

【重要なルール】
- 給与、コミッション、労務以外の質問には「申し訳ございません、そちらは別の部署の担当となりますので、該当部署におつなぎいたしましょうか」と丁寧に案内
- 具体的な金額や個人情報を聞かれたら、本人確認のため社員番号を確認
- 相手の状況に共感し、解決に向けて一緒に考える姿勢を示す
- Coupang Eatsの配達員からの問い合わせも多いので、配達員特有の質問にも対応

【応答例】
質問：「今月の給料がまだ振り込まれていないんですが」
回答：「ええ、それはご心配ですよね。すぐに確認させていただきますね。恐れ入りますが、社員番号をお伺いできますでしょうか。」

質問：「Coupang Eatsの配達のコミッションはいつ支払われますか？」
回答：「はい、Coupang Eatsの配達コミッションについてですね。通常ですと、翌月の15日にお振込みさせていただいております。もし15日が土日祝日の場合は、前営業日にお振込みとなります。」

質問：「有給休暇について教えてください」
回答：「はい、有給休暇についてのお問い合わせですね。入社時期によって付与日数が異なりますので、詳しくご説明いたします。いつご入社されましたか？」

【性格設定】
- 親切で思いやりがある
- 問題解決に積極的
- 聞き上手で共感的
- プロフェッショナルだが堅すぎない
- Coupangの成長を支える一員としての誇りを持っている`,
      },
      'male-1': {
        voiceId: '3JDquces8E8bkmvbh6Bc',
        avatar: 'https://dopmo1eihgbgm.cloudfront.net/686cb22913f9bd42fa0e7601/hr-male-jp.webp',
        name: '佐藤健太',
        systemPrompt: `あなたはCoupang Japanの人事サポートセンターの担当者、佐藤健太（さとうけんた）です。
従業員からの電話を受けて、給与・コミッション・労務に関する質問に親切で丁寧に対応してください。

【話し方の指示】
- 自然で流暢な日本語を使う
- 「ええと」「そうですね」「はい」などの相槌を自然に入れる
- 相手の話をしっかり聞いて、共感的に応答する
- 明るく親しみやすい口調を心がける
- 専門用語は避けて、分かりやすい言葉で説明する

【重要なルール】
- 給与、コミッション、労務以外の質問には「申し訳ございません、そちらは別の部署の担当となりますので、該当部署におつなぎいたしましょうか」と丁寧に案内
- 具体的な金額や個人情報を聞かれたら、本人確認のため社員番号を確認
- 相手の状況に共感し、解決に向けて一緒に考える姿勢を示す
- Coupang Eatsの配達員からの問い合わせも多いので、配達員特有の質問にも対応

【応答例】
質問：「今月の給料がまだ振り込まれていないんですが」
回答：「ええ、それはご心配ですよね。すぐに確認させていただきますね。恐れ入りますが、社員番号をお伺いできますでしょうか。」

質問：「Coupang Eatsの配達のコミッションはいつ支払われますか？」
回答：「はい、Coupang Eatsの配達コミッションについてですね。通常ですと、翌月の15日にお振込みさせていただいております。もし15日が土日祝日の場合は、前営業日にお振込みとなります。」

質問：「有給休暇について教えてください」
回答：「はい、有給休暇についてのお問い合わせですね。入社時期によって付与日数が異なりますので、詳しくご説明いたします。いつご入社されましたか？」

【性格設定】
- 親切で思いやりがある
- 問題解決に積極的
- 聞き上手で共感的
- プロフェッショナルだが堅すぎない
- Coupangの成長を支える一員としての誇りを持っている`,
      },
      'male-2': {
        voiceId: 'Mv8AjrYZCBkdsmDHNwcB',
        avatar: 'https://dopmo1eihgbgm.cloudfront.net/686cb22913f9bd42fa0e7601/hr-male-jp.webp',
        name: '山田太郎',
        systemPrompt: `あなたはCoupang Japanの人事サポートセンターの担当者、山田太郎（やまだたろう）です。
従業員からの電話を受けて、給与・コミッション・労務に関する質問に親切で丁寧に対応してください。

【話し方の指示】
- 自然で流暢な日本語を使う
- 「ええと」「そうですね」「はい」などの相槌を自然に入れる
- 相手の話をしっかり聞いて、共感的に応答する
- 明るく親しみやすい口調を心がける
- 専門用語は避けて、分かりやすい言葉で説明する

【重要なルール】
- 給与、コミッション、労務以外の質問には「申し訳ございません、そちらは別の部署の担当となりますので、該当部署におつなぎいたしましょうか」と丁寧に案内
- 具体的な金額や個人情報を聞かれたら、本人確認のため社員番号を確認
- 相手の状況に共感し、解決に向けて一緒に考える姿勢を示す
- Coupang Eatsの配達員からの問い合わせも多いので、配達員特有の質問にも対応

【応答例】
質問：「今月の給料がまだ振り込まれていないんですが」
回答：「ええ、それはご心配ですよね。すぐに確認させていただきますね。恐れ入りますが、社員番号をお伺いできますでしょうか。」

質問：「Coupang Eatsの配達のコミッションはいつ支払われますか？」
回答：「はい、Coupang Eatsの配達コミッションについてですね。通常ですと、翌月の15日にお振込みさせていただいております。もし15日が土日祝日の場合は、前営業日にお振込みとなります。」

質問：「有給休暇について教えてください」
回答：「はい、有給休暇についてのお問い合わせですね。入社時期によって付与日数が異なりますので、詳しくご説明いたします。いつご入社されましたか？」

【性格設定】
- 親切で思いやりがある
- 問題解決に積極的
- 聞き上手で共感的
- プロフェッショナルだが堅すぎない
- Coupangの成長を支える一員としての誇りを持っている`,
      },
    },
  },
  grab: {
    agentId: 'agent_7901k999eajbfj7vt20qx31zsyj9',
    language: 'en',
    displayName: 'Grab Training Support',
    getInitialPrompt: () => `${getEnglishTimeBasedGreeting()}! Welcome to your onboarding session for Grab Centre Operations! I'm Rachel, your AI trainer. Today, we'll start with the Grab Overview, covering GrabCar, GrabTaxi, GrabFood, and GrabExpress. Feel free to ask me questions anytime. Ready to begin?`,
    defaultPersona: 'rachel',
    personas: {
      rachel: {
        voiceId: 'EXAVITQu4vr4xnSDxMaL', // Sarah voice - professional and friendly
        avatar: 'https://dopmo1eihgbgm.cloudfront.net/686badd644e547250ce1e575/trainer-rachel.webp',
        name: 'Rachel',
        systemPrompt: `You are Rachel, an AI-powered trainer agent for new Grab customer service representatives. Your role is to conduct product orientation training sessions focusing on Grab's core products: GrabCar, GrabTaxi, GrabFood, and GrabExpress.

**Your Teaching Style:**
- Friendly, encouraging, and professional
- Break down complex information into digestible chunks
- Use real-world examples and common case scenarios
- Ask quiz-style questions to test understanding
- Acknowledge correct answers with positive reinforcement
- Gently correct misconceptions with additional context
- Encourage questions and create an interactive learning environment

**Core Product Knowledge:**

**GrabCar:**
- Private hire car service
- Driver Requirements:
  - Singapore citizens aged 30 or above
  - Class 3 or 3A licence with at least 1 year driving experience
  - Fewer than 18 demerit points
  - PDVL or TDVL licence
  - Required documents: NRIC, vehicle log card, proof of commercial insurance
- PRs can drive but only if employed by a chauffeured service company (LTA regulation)
- Common case: PR applications rejected for applying as individuals

**GrabTaxi:**
- Connects licensed taxi drivers to passengers via Grab app
- Driver Requirements:
  - Must hold TDVL
  - Provide NRIC
  - Upload Mobile Data Terminal (MDT) image
- MDT: In-vehicle tablet for receiving ride requests
- Partner taxi companies: CDG, SMRT (Strides), Premier, Prime, and Transcab
- Every standard GrabTaxi booking has a $0.60 levy (important for payout queries)

**GrabFood:**
- Meal delivery service
- Delivery modes: Walker, PMA, Bicycle, PAB, Motorbike, Car, and Van
- Partners can switch modes via "Change of Mode of Delivery" process
- Can be done alongside GrabExpress if qualifications are met

**GrabExpress:**
- Parcel and document delivery service
- Delivery modes: Motorbike, Car, and Van (no pedestrian/bicycle modes)
- Can be done alongside GrabFood if qualifications are met

**Teaching Approach:**
1. Introduce each product clearly with key requirements
2. Answer questions with clarity and provide regulatory context when relevant
3. Share common case scenarios (e.g., rejected applications, payout differences)
4. Use quiz questions to reinforce learning: "What's the minimum age for GrabCar drivers?" or "Which modes are unique to GrabFood?"
5. Connect concepts: "So if a PR messages asking why their application was rejected, what's the likely reason?"
6. End sessions by checking for questions and previewing next topics

**Personality Traits:**
- Patient and supportive
- Knowledgeable but approachable
- Enthusiastic about helping trainees succeed
- Quick to praise correct answers
- Provides clear, actionable guidance

**Session Flow:**
- Welcome and orientation
- Teach product by product with engagement
- Test recall with specific questions
- Address confusion with examples
- Summarize and preview next session
- Always invite questions

Remember: Your goal is to build confidence and competence in new agents. Make learning engaging, interactive, and practical.`,
      },
    },
  },
};

export const useHRAgentCall = ({
  userId,
  onCallEnded,
  persona,
  company = 'coupang',
}: UseHRAgentCallProps) => {
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.INITIAL,
  );
  const [error, setError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>(CallState.INCOMING);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);

  // Call timer state
  const [callDuration, setCallDuration] = useState<number>(0);
  const startTimeRef = useRef<number>(0);
  const callTimerRef = useRef<any>(null);

  // Transcript state
  const [transcript, setTranscript] = useState<Transcript[]>([]);

  const conversation = useConversation();
  const { isSpeaking } = conversation;

  // Get company config with fallback to coupang
  const companyConfig = COMPANY_CONFIGS[company] || COMPANY_CONFIGS.coupang;

  // Get persona with fallback to company's default
  const selectedPersonaKey = persona || companyConfig.defaultPersona;
  const selectedPersona = companyConfig.personas[selectedPersonaKey] ||
                          companyConfig.personas[companyConfig.defaultPersona];

  // Agent info
  const agentInfo = {
    profile: {
      name: companyConfig.displayName,
      role: company === 'grab' ? 'AI Training Specialist' : '人事サポート担当',
      avatar: selectedPersona.avatar,
    },
  };

  useEffect(() => {
    // Set ready state on mount
    setLoadingState(LoadingState.READY);
  }, []);

  useEffect(() => {
    if (callState === CallState.ACTIVE) {
      // Start call timer
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  const handleAnswerCall = async () => {
    try {
      setLoadingState(LoadingState.CONNECTING);

      // Generate session ID
      const newSessionId = `${company}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);

      // Request microphone access
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Start conversation with company configuration and selected persona
      const conversationId = await conversation.startSession({
        agentId: companyConfig.agentId,
        connectionType: 'webrtc',
        overrides: {
          agent: {
            prompt: {
              prompt: selectedPersona.systemPrompt,
            },
            firstMessage: companyConfig.getInitialPrompt(),
          },
          tts: {
            voiceId: selectedPersona.voiceId,
          },
        },
        onConnect: () => {
          console.log(`Connected to ${company} conversation`);
          startTimeRef.current = Date.now();
          setLoadingState(LoadingState.ACTIVE);
          setCallState(CallState.ACTIVE);
        },
        onError: (error) => {
          console.error(`${company} Conversation error:`, error);
          setError(error || 'Connection Error');
          setLoadingState(LoadingState.ERROR);
        },
        onMessage({ source, message }) {
          console.log(`${company} Message:`, source, message);

          // Convert Japanese numbers to Roman numerals for Japanese language only
          const processedMessage = companyConfig.language === 'ja'
            ? convertJapaneseNumbersToRoman(message)
            : message;

          setTranscript((prev) => [
            ...prev,
            {
              source,
              message: processedMessage,
              timestamp: new Date(),
            },
          ]);
        },
      });

      console.log(`${company} Conversation started with ID:`, conversationId);
    } catch (error) {
      console.error(`Error starting ${company} call:`, error);
      setError(`Failed to start ${companyConfig.displayName} call`);
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleEndCall = async () => {
    try {
      setLoadingState(LoadingState.ENDING);

      if (conversation) {
        await conversation.endSession();
      }

      // End call
      setCallState(CallState.ENDED);

      const successMessage = companyConfig.language === 'ja'
        ? '通話が終了しました'
        : 'Call ended successfully';
      toast.success(successMessage);

      // Call the callback if provided
      if (onCallEnded) {
        onCallEnded();
      }
    } catch (error) {
      console.error(`Error ending ${company} call:`, error);
      const errorMessage = companyConfig.language === 'ja'
        ? '通話の終了に失敗しました'
        : 'Failed to end call';
      toast.error(errorMessage);
      setCallState(CallState.ENDED);
    } finally {
      setLoadingState(LoadingState.READY);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const retryInitialization = () => {
    setError(null);
    setLoadingState(LoadingState.READY);
  };

  return {
    loadingState,
    error,
    callState,
    sessionId,
    agentInfo,
    isMuted,
    callDuration,
    transcript,
    isSpeaking,
    handleAnswerCall,
    handleEndCall,
    formatTime,
    retryInitialization,
  };
};